import React, { useState, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import axios from 'axios';
import { createApiUrlWithPrefix } from '../config/api';
import { toast } from 'react-hot-toast';
import { X, Save, Shield, Check, X as XIcon, BarChart3, CreditCard, Users, Building, Package, Calendar, ClipboardCheck, UserCheck, Users2, DollarSign } from 'lucide-react';

const UserPermissionsDialog = ({ isOpen, onClose, user, onPermissionsUpdated }) => {
  const { t, i18n } = useTranslation();
  const isRTL = i18n.language === 'ar';

  const [permissions, setPermissions] = useState([]);
  const [userPermissions, setUserPermissions] = useState([]);
  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);
  const [seeding, setSeeding] = useState(false);

  useEffect(() => {
    if (isOpen && user) {
      console.log('Dialog opened for user:', user);
      fetchPermissions();
      fetchUserPermissions();
    } else {
      console.log('Dialog closed, resetting permissions');
      setUserPermissions([]);
    }
  }, [isOpen, user]);

  const fetchPermissions = async () => {
    try {
      setLoading(true);
      const token = localStorage.getItem('token');
      const response = await axios.get(createApiUrlWithPrefix('permission/permissions'), {
        headers: { Authorization: `Bearer ${token}` }
      });
      setPermissions(response.data || []);
    } catch (error) {
      console.error('Error fetching permissions:', error);
      toast.error('Failed to load permissions');
    } finally {
      setLoading(false);
    }
  };

  const fetchUserPermissions = async () => {
    if (!user) return;
    
    try {
      const token = localStorage.getItem('token');
      const response = await axios.get(createApiUrlWithPrefix(`permission/users/${user.id}/permissions`), {
        headers: { Authorization: `Bearer ${token}` }
      });
      
      // Convert to a simple array of permission IDs that are granted
      // The API returns { userPermissions: [...] } structure
      const userPermissionsData = response.data.userPermissions || response.data;
      const grantedPermissions = Array.isArray(userPermissionsData) 
        ? userPermissionsData
            .filter(up => up.isGranted)
            .map(up => up.permissionId)
        : [];
      
      console.log('Fetched user permissions response:', response.data);
      console.log('User permissions data:', userPermissionsData);
      console.log('Granted permissions:', grantedPermissions);
      setUserPermissions(grantedPermissions);
    } catch (error) {
      console.error('Error fetching user permissions:', error);
      // Don't show error toast for this, as user might not have any permissions yet
    }
  };

  const handlePermissionToggle = (permissionId) => {
    console.log('Toggling permission:', permissionId);
    console.log('Current userPermissions:', userPermissions);
    
    setUserPermissions(prev => {
      const newPermissions = prev.includes(permissionId) 
        ? prev.filter(id => id !== permissionId)
        : [...prev, permissionId];
      
      console.log('New userPermissions:', newPermissions);
      return newPermissions;
    });
  };

  const handleSavePermissions = async () => {
    if (!user) return;

    try {
      setSaving(true);
      const token = localStorage.getItem('token');
      
      await axios.post(createApiUrlWithPrefix(`permission/users/${user.id}/permissions/bulk`), {
        userId: user.id,
        permissionIds: userPermissions,
        isGranted: true
      }, {
        headers: { Authorization: `Bearer ${token}` }
      });

      toast.success('Permissions updated successfully');
      onPermissionsUpdated?.();
    } catch (error) {
      console.error('Error saving permissions:', error);
      toast.error('Failed to save permissions');
    } finally {
      setSaving(false);
    }
  };

  const handleSeedPermissions = async () => {
    try {
      setSeeding(true);
      const token = localStorage.getItem('token');
      const response = await axios.post(createApiUrlWithPrefix('seed/permissions'), {}, {
        headers: { Authorization: `Bearer ${token}` }
      });
      
      toast.success(response.data.message);
      // Refresh permissions after seeding
      await fetchPermissions();
    } catch (error) {
      console.error('Error seeding permissions:', error);
      toast.error(error.response?.data?.message || 'Failed to seed permissions');
    } finally {
      setSeeding(false);
    }
  };

  // Simplified permission groups based on navigation structure
  const permissionGroups = [
    {
      title: 'Main Navigation',
      icon: BarChart3,
      color: 'blue',
      permissions: [
        { name: 'Access Reports', icon: BarChart3, description: 'View reports and analytics' },
        { name: 'Access Payments', icon: CreditCard, description: 'Access payments section' },
        { name: 'Access Sessions', icon: Calendar, description: 'Manage sessions' },
        { name: 'Access Attendance', icon: ClipboardCheck, description: 'Manage attendance' }
      ]
    },
    {
      title: 'User Management',
      icon: Users,
      color: 'purple',
      permissions: [
        { name: 'Access Personnel Management', icon: Users2, description: 'Manage personnel' },
        { name: 'Access Coaches Management', icon: UserCheck, description: 'Manage coaches' },
        { name: 'Access Riders Management', icon: Users, description: 'Manage riders' },
        { name: 'Access Owners Management', icon: Users2, description: 'Manage owners' },
        { name: 'Access Temporary Users Management', icon: Users, description: 'Manage temporary users' }
      ]
    },
    {
      title: 'Payment Sub-sections',
      icon: DollarSign,
      color: 'green',
      permissions: [
        { name: 'Access Rider Payments', icon: CreditCard, description: 'Manage rider payments' },
        { name: 'Access Owner Payments', icon: DollarSign, description: 'Manage owner payments' },
        { name: 'Access Temporary User Payments', icon: CreditCard, description: 'Manage temporary user payments' },
        { name: 'Access Miscellaneous Payments', icon: Building, description: 'Manage miscellaneous payments' }
      ]
    },
    {
      title: 'Institutional',
      icon: Building,
      color: 'orange',
      permissions: [
        { name: 'Access Institutional Personnel', icon: Users2, description: 'Manage institutional personnel' },
        { name: 'Access Institutional Other', icon: Building, description: 'Manage institutional other' },
        { name: 'Access Inventory Management', icon: Package, description: 'Manage inventory' }
      ]
    }
  ];

  // Helper function to get permission by name
  const getPermissionByName = (name) => {
    return permissions.find(p => p.name === name);
  };

  if (!isOpen || !user) return null;

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50" dir={isRTL ? 'rtl' : 'ltr'}>
      <div className="bg-white rounded-lg shadow-xl max-w-4xl w-full mx-4 max-h-[90vh] overflow-hidden">
        {/* Header */}
        <div className="flex items-center justify-between p-6 border-b border-gray-200">
          <div className="flex items-center">
            <Shield className="h-6 w-6 text-primary-600 mr-3" />
            <div>
              <h2 className="text-xl font-semibold text-gray-900">
                Manage Permissions
              </h2>
              <p className="text-sm text-gray-600">
                {user.name} ({user.email})
              </p>
            </div>
          </div>
          <button
            onClick={onClose}
            className="text-gray-400 hover:text-gray-600 transition-colors"
          >
            <X className="h-6 w-6" />
          </button>
        </div>

        {/* Content */}
        <div className="p-6 overflow-y-auto max-h-[calc(90vh-140px)]">
          {loading ? (
            <div className="flex items-center justify-center py-8">
              <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary-600"></div>
            </div>
          ) : (
            <div className="space-y-6">
              {/* Permission Summary */}
              <div className="bg-blue-50 border border-blue-200 rounded-lg p-4">
                <div className="flex items-center justify-between">
                  <div>
                    <h3 className="text-sm font-medium text-blue-900">Permission Summary</h3>
                    <p className="text-sm text-blue-700">
                      {userPermissions.length} of 15 permissions granted
                    </p>
                  </div>
                  <div className="flex items-center gap-2">
                    <div className="text-2xl font-bold text-blue-600">
                      {userPermissions.length}/15
                    </div>
                    <div className="flex gap-2">
                      <button
                        onClick={() => {
                          const allPermissionIds = permissions.map(p => p.id);
                          setUserPermissions(allPermissionIds);
                        }}
                        className="px-3 py-1 text-xs bg-green-600 text-white rounded hover:bg-green-700"
                      >
                        Grant All
                      </button>
                      <button
                        onClick={() => {
                          setUserPermissions([]);
                        }}
                        className="px-3 py-1 text-xs bg-red-600 text-white rounded hover:bg-red-700"
                      >
                        Revoke All
                      </button>
                    </div>
                  </div>
                </div>
              </div>
              
              {/* Permission Groups */}
              {permissionGroups.map((group) => {
                const groupPermissions = group.permissions.map(p => getPermissionByName(p.name)).filter(Boolean);
                const groupPermissionIds = groupPermissions.map(p => p.id);
                const grantedInGroup = groupPermissionIds.filter(id => userPermissions.includes(id));
                const allGranted = grantedInGroup.length === groupPermissionIds.length;
                const someGranted = grantedInGroup.length > 0;
                
                const colorClasses = {
                  blue: 'border-blue-200 bg-blue-50',
                  purple: 'border-purple-200 bg-purple-50',
                  green: 'border-green-200 bg-green-50',
                  orange: 'border-orange-200 bg-orange-50'
                };
                
                const iconColors = {
                  blue: 'text-blue-600',
                  purple: 'text-purple-600',
                  green: 'text-green-600',
                  orange: 'text-orange-600'
                };
                
                return (
                  <div key={group.title} className={`border rounded-lg p-4 ${colorClasses[group.color]}`}>
                    <div className="flex items-center justify-between mb-4">
                      <div className="flex items-center gap-2">
                        <group.icon className={`h-5 w-5 ${iconColors[group.color]}`} />
                        <h3 className="text-lg font-semibold text-gray-900">
                          {group.title}
                        </h3>
                      </div>
                      <div className="flex items-center gap-2">
                        <span className="text-sm text-gray-500">
                          {grantedInGroup.length}/{groupPermissionIds.length} granted
                        </span>
                        <div className="flex gap-1">
                          <button
                            onClick={() => {
                              setUserPermissions(prev => {
                                const otherPermissions = prev.filter(id => !groupPermissionIds.includes(id));
                                return [...otherPermissions, ...groupPermissionIds];
                              });
                            }}
                            className={`px-2 py-1 text-xs rounded ${
                              allGranted 
                                ? 'bg-green-100 text-green-700' 
                                : 'bg-gray-100 text-gray-600 hover:bg-gray-200'
                            }`}
                          >
                            All
                          </button>
                          <button
                            onClick={() => {
                              setUserPermissions(prev => 
                                prev.filter(id => !groupPermissionIds.includes(id))
                              );
                            }}
                            className={`px-2 py-1 text-xs rounded ${
                              !someGranted 
                                ? 'bg-red-100 text-red-700' 
                                : 'bg-gray-100 text-gray-600 hover:bg-gray-200'
                            }`}
                          >
                            None
                          </button>
                        </div>
                      </div>
                    </div>
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
                      {group.permissions.map((permissionInfo) => {
                        const permission = getPermissionByName(permissionInfo.name);
                        if (!permission) return null;
                        
                        const isGranted = userPermissions.includes(permission.id);
                        const IconComponent = permissionInfo.icon;
                        
                        return (
                          <div
                            key={permission.id}
                            className={`flex items-center justify-between p-3 rounded-lg border transition-colors cursor-pointer ${
                              isGranted 
                                ? 'bg-green-50 border-green-200' 
                                : 'bg-gray-50 border-gray-200 hover:bg-gray-100'
                            }`}
                            onClick={() => handlePermissionToggle(permission.id)}
                          >
                            <div className="flex items-center gap-3 flex-1">
                              <IconComponent className={`h-4 w-4 ${isGranted ? 'text-green-600' : 'text-gray-400'}`} />
                              <div>
                                <div className="font-medium text-gray-900">
                                  {permissionInfo.name.replace('Access ', '')}
                                </div>
                                <div className="text-sm text-gray-500">
                                  {permissionInfo.description}
                                </div>
                              </div>
                            </div>
                            <div className={`ml-3 p-2 rounded-full transition-colors ${
                              isGranted 
                                ? 'bg-green-100 text-green-600 border-2 border-green-300' 
                                : 'bg-gray-100 text-gray-400 border-2 border-gray-300'
                            }`}>
                              {isGranted ? (
                                <Check className="h-4 w-4" />
                              ) : (
                                <XIcon className="h-4 w-4" />
                              )}
                            </div>
                          </div>
                        );
                      })}
                    </div>
                  </div>
                );
              })}
            </div>
          )}
        </div>

        {/* Footer */}
        <div className="flex items-center justify-end gap-3 p-6 border-t border-gray-200 bg-gray-50">
          <button
            onClick={onClose}
            className="px-4 py-2 text-sm font-medium text-gray-700 bg-white border border-gray-300 rounded-md hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-primary-500"
          >
            Cancel
          </button>
          <button
            onClick={handleSavePermissions}
            disabled={saving || loading}
            className="flex items-center px-4 py-2 text-sm font-medium text-white bg-primary-600 border border-transparent rounded-md hover:bg-primary-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-primary-500 disabled:opacity-50 disabled:cursor-not-allowed"
          >
            {saving ? (
              <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-white mr-2"></div>
            ) : (
              <Save className="h-4 w-4 mr-2" />
            )}
            Save Changes
          </button>
        </div>
      </div>
    </div>
  );
};

export default UserPermissionsDialog;
